import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, ExternalLink, Building2, Flame, Ruler, Calendar, Shield, Home, BookOpen, Camera, Tag } from "lucide-react";
import {
    getProperty, getEntity, listUnits, listTenants, listWiki, aktuelleMieten,
} from "@/lib/api";
import { euro, germanDate, statusLabel, istAktivesMietverhaeltnis } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import WikiPanel from "@/components/WikiPanel";
import PhotoTimeline from "@/components/PhotoTimeline";

const KV = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100">
        {Icon && <Icon size={14} className="text-gray-400 mt-0.5" />}
        <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500">{label}</div>
            <div className="text-sm text-slate-900 mt-0.5">{value ?? "—"}</div>
        </div>
    </div>
);

const PropertyDetail = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const tabParam = searchParams.get("tab");
    const initialTab = tabParam === "wiki" || tabParam === "fotos" ? tabParam : "overview";
    const highlightEntry = searchParams.get("entry") || null;
    const [tab, setTab] = useState(initialTab);
    const [wikiHighlight, setWikiHighlight] = useState(highlightEntry);
    const [property, setProperty] = useState(null);
    const [entity, setEntity] = useState(null);
    const [units, setUnits] = useState([]);
    const [tenants, setTenants] = useState([]);
    // Wo eine Mietanpassung wirksam ist, gilt deren Betrag -- nicht der am
    // Mietverhältnis. Ohne das steht hier die Vertragsmiete, während das
    // Soll längst mit der Erhöhung rechnet.
    const [mieten, setMieten] = useState({});
    // Die Wiki-Einträge werden hier gehalten und an WikiPanel und PhotoTimeline
    // durchgegeben — vorher hat jede der drei Stellen dieselbe Liste einzeln
    // geholt. `null` heißt "lädt noch".
    const [wikiEntries, setWikiEntries] = useState(null);
    const [wikiError, setWikiError] = useState(null);

    const loadWiki = useCallback(async () => {
        try {
            setWikiEntries(await listWiki({ scope: "property", property_id: id }));
            setWikiError(null);
        } catch (e) {
            setWikiError(e?.message || String(e));
        }
    }, [id]);

    useEffect(() => {
        (async () => {
            const p = await getProperty(id);
            setProperty(p);
            const [e, u, t, m] = await Promise.all([
                getEntity(p.entity_id),
                listUnits({ property_id: id }),
                listTenants(),
                aktuelleMieten().catch(() => ({ mieten: {} })),
                loadWiki(),
            ]);
            setEntity(e); setUnits(u); setTenants(t); setMieten(m.mieten || {});
        })();
    }, [id, loadWiki]);

    const soldEntry = useMemo(() => {
        const sold = (wikiEntries || []).filter((e) => e.category === "verkauf-notariat");
        if (sold.length === 0) return null;
        return [...sold].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];
    }, [wikiEntries]);

    const tenantByUnit = useMemo(() => {
        const m = {};
        tenants.filter(istAktivesMietverhaeltnis).forEach((t) => {
            if (t.unit_id) m[t.unit_id] = t;
        });
        return m;
    }, [tenants]);

    const kaltVon = (t) => (t ? (mieten[t.id]?.kaltmiete ?? t.kaltmiete ?? 0) : 0);
    const sumMiete = useMemo(() => {
        return units.reduce((acc, u) => acc + kaltVon(tenantByUnit[u.id]), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [units, tenantByUnit, mieten]);

    if (!property) return <div className="p-10 text-gray-400">Lade…</div>;

    return (
        <div className="px-6 md:px-10 py-8 space-y-8">
            <Link to="/immobilien" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-slate-900">
                <ArrowLeft size={14} /> Zurück zu Immobilien
            </Link>

            <div className="flex items-start justify-between gap-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">{property.asset_typ}</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900 flex items-center gap-3" data-testid="property-detail-title">
                        {property.label || property.address}
                        {soldEntry && (
                            <span className="inline-flex items-center gap-1 text-[11px] bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-2.5 py-1 font-medium" data-testid="sold-badge-detail">
                                <Tag size={10} /> Verkauft · {germanDate(soldEntry.created_at)}
                            </span>
                        )}
                    </h1>
                    <p className="text-sm text-gray-600 mt-1">{property.address}</p>
                    {entity && (
                        <Link to={`/entitaeten`} className="inline-flex items-center gap-1.5 mt-2 text-xs text-slate-600 hover:text-slate-900">
                            <Building2 size={12} /> Eigentümer: <span className="font-medium">{entity.name}</span>
                        </Link>
                    )}
                </div>
                {property.bestandsfotos_link && (
                    <a href={property.bestandsfotos_link} target="_blank" rel="noreferrer">
                        <Button variant="outline" size="sm"><ExternalLink size={14} className="mr-1.5" /> Bestandsfotos</Button>
                    </a>
                )}
            </div>

            <Tabs value={tab} onValueChange={setTab} className="space-y-6">
                <TabsList data-testid="property-tabs">
                    <TabsTrigger value="overview" data-testid="tab-overview">Übersicht & Einheiten</TabsTrigger>
                    <TabsTrigger value="wiki" data-testid="tab-wiki">
                        <BookOpen size={13} className="mr-1.5" /> Wiki
                    </TabsTrigger>
                    <TabsTrigger value="fotos" data-testid="tab-fotos">
                        <Camera size={13} className="mr-1.5" /> Fotos
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="overview">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 space-y-4">
                    <div className="bg-white border border-gray-200 rounded-md p-5">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-3">Stammdaten</div>
                        <KV icon={Building2} label="Asset-Typ" value={property.asset_typ} />
                        <KV icon={Calendar} label="Baujahr" value={property.year_built} />
                        <KV icon={Flame} label="Energieträger" value={property.fuel_type} />
                        <KV icon={Shield} label="Versicherung" value={property.versicherung_gesellschaft ? `${property.versicherung_gesellschaft}${property.versicherung_police ? " · " + property.versicherung_police : ""}` : null} />
                        {property.weg_verwaltung && <KV label="WEG-Verwaltung" value={property.weg_verwaltung} />}
                        {/* Vier Daten, vier Wirkungen (05.10.2026): Besitzuebergang steuert
                            das Soll, Eigentumsumschreibung ist das Grundbuch. */}
                        {property.besitz_ab && <KV icon={Calendar} label="Besitzübergang (Kauf)" value={germanDate(property.besitz_ab)} />}
                        {property.umschreibung_ab && <KV label="Eigentumsumschreibung (Kauf)" value={germanDate(property.umschreibung_ab)} />}
                        {property.besitz_bis && <KV icon={Calendar} label="Besitzübergang (Verkauf)" value={germanDate(property.besitz_bis)} />}
                        {property.umschreibung_bis && <KV label="Eigentumsumschreibung (Verkauf)" value={germanDate(property.umschreibung_bis)} />}
                        {property.grundbuch_eintrag && (
                            <div className="py-3 border-b border-gray-100">
                                <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Grundbuch-Eintrag</div>
                                <div className="text-sm text-slate-900 mt-1 font-mono leading-relaxed">{property.grundbuch_eintrag}</div>
                            </div>
                        )}
                        {property.energieausweis_link && (
                            <div className="py-3 border-b border-gray-100">
                                <a href={property.energieausweis_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900"><ExternalLink size={12} /> Energieausweis</a>
                            </div>
                        )}
                    </div>

                    <div className="bg-white border border-gray-200 rounded-md p-5">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-3">Performance</div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <div className="kachel-label uppercase text-gray-400">Einheiten</div>
                                <div className="text-2xl font-bold text-slate-900 tabular-nums">{units.length}</div>
                            </div>
                            <div>
                                <div className="kachel-label uppercase text-gray-400" title="Kaltmiete pro Monat">Kaltmiete/Mo.</div>
                                {/* Halbe Kachelbreite, ein voller Geldbetrag: bei
                                    1100 px brauchte "2.821,00 EUR" 122 px in einer
                                    88 px breiten Spalte. */}
                                <div className="text-lg xl:text-2xl font-bold text-slate-900 tabular-nums break-words">
                                    {euro(sumMiete)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-2">
                    <div className="bg-white border border-gray-200 rounded-md">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                            <div className="font-heading font-semibold text-slate-900">Mieteinheiten</div>
                            <Link to={`/einheiten?property_id=${property.id}`}>
                                <Button variant="outline" size="sm" className="h-8" data-testid="btn-manage-units">Verwalten</Button>
                            </Link>
                        </div>
                        {/* Der Rahmen scrollt waagerecht: fuenf Spalten passen
                            in der schmalen Spalte des Detailfensters nicht. */}
                        <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-[11px] uppercase tracking-[0.12em] text-gray-500">
                                <tr>
                                    <th className="text-left font-medium px-5 py-2.5">Einheit</th>
                                    <th className="text-left font-medium px-5 py-2.5">Typ</th>
                                    <th className="text-left font-medium px-5 py-2.5">Status</th>
                                    <th className="text-left font-medium px-5 py-2.5">Aktueller Mieter</th>
                                    <th className="text-left font-medium px-5 py-2.5">Kaltmiete</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {(() => {
                                    // Aufsteigend nach Bezeichnung: TE 01 oben, WE 05 unten.
                                    // Vorher kam die Ladereihenfolge der Datenbank durch, und
                                    // die zeigte das Haus von oben nach unten verkehrt herum.
                                    // `numeric` sorgt dafuer, dass WE 10 hinter WE 09 steht.
                                    const standalone = units
                                        .filter((u) => !u.parent_unit_id)
                                        .sort((a, b) => (a.unit_label || "").localeCompare(
                                            b.unit_label || "", "de", { numeric: true, sensitivity: "base" }));
                                    const accByParent = {};
                                    units.filter((u) => u.parent_unit_id).forEach((u) => {
                                        if (!accByParent[u.parent_unit_id]) accByParent[u.parent_unit_id] = [];
                                        accByParent[u.parent_unit_id].push(u);
                                    });
                                    const rows = [];
                                    standalone.forEach((u) => {
                                        rows.push({ unit: u, accessory: false, accCount: (accByParent[u.id] || []).length });
                                        (accByParent[u.id] || []).forEach((a) => rows.push({ unit: a, accessory: true }));
                                    });
                                    // accessories whose parent isn't in this property (cross-property unlikely but defensive)
                                    units.filter((u) => u.parent_unit_id && !standalone.some((s) => s.id === u.parent_unit_id))
                                         .forEach((a) => rows.push({ unit: a, accessory: true, orphan: true }));
                                    return rows;
                                })().map(({ unit: u, accessory, accCount, orphan }) => {
                                    const t = tenantByUnit[u.id];
                                    return (
                                        <tr key={u.id} className={`hover:bg-gray-50 ${accessory ? "bg-amber-50/30" : ""}`}>
                                            <td className="px-5 py-3">
                                                <div className="flex items-center gap-2">
                                                    {accessory && (
                                                        <span className="text-amber-700 text-xs font-mono">└─</span>
                                                    )}
                                                    <Home size={12} className={accessory ? "text-amber-500" : "text-gray-400"} />
                                                    <Link to={`/einheiten/${u.id}`}
                                                          className="font-medium text-slate-900 hover:underline"
                                                          data-testid={`prop-unit-link-${u.id}`}>
                                                        {u.unit_label || "Einheit"}
                                                    </Link>
                                                    {accessory && !u.eigenes_grundbuch && (
                                                        <span className="text-[10px] bg-amber-100 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5">
                                                            Zubehör · kein eigenes Grundbuch
                                                        </span>
                                                    )}
                                                    {accessory && u.eigenes_grundbuch && (
                                                        <span className="text-[10px] bg-blue-50 text-blue-800 border border-blue-200 rounded px-1.5 py-0.5">
                                                            Mit-Übergabe · eig. Grundbuch
                                                        </span>
                                                    )}
                                                    {!accessory && accCount > 0 && (
                                                        <span className="text-[10px] bg-slate-100 text-slate-700 border border-slate-200 rounded px-1.5 py-0.5">
                                                            +{accCount} Zubehör
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-5 py-3 text-gray-700 text-xs">{statusLabel(u.typ)}</td>
                                            <td className="px-5 py-3"><StatusBadge status={u.status} /></td>
                                            <td className="px-5 py-3 text-gray-700">
                                                {t ? (
                                                    // Zum Mietverhaeltnis: /mieter?edit=<id> oeffnet den
                                                    // Bearbeiten-Dialog, in dem die Mieterdaten stehen.
                                                    <Link to={`/mieter?edit=${t.id}`} className="hover:underline"
                                                          data-testid={`prop-tenant-link-${u.id}`}
                                                          title="Mietverhältnis öffnen">
                                                        {t.full_name}
                                                    </Link>
                                                ) : "—"}
                                            </td>
                                            <td className="px-5 py-3 text-gray-700 tabular-nums">
                                                {t ? euro(kaltVon(t)) : "—"}
                                                {t && mieten[t.id] && (
                                                    <div className="text-[10px] text-emerald-700"
                                                         title={`Im Mietverhältnis stehen ${euro(t.kaltmiete)}.`}>
                                                        angepasst ab {mieten[t.id].gilt_ab}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {units.length === 0 && (
                                    <tr><td colSpan={5} className="px-5 py-6 text-center text-gray-400 text-sm">Noch keine Mieteinheiten angelegt.</td></tr>
                                )}
                            </tbody>
                        </table>
                        </div>
                    </div>
                </div>
                    </div>
                </TabsContent>

                <TabsContent value="wiki">
                    <WikiPanel
                        scope="property"
                        scopeId={property.id}
                        scopeLabel={property.label || property.address}
                        highlightEntry={wikiHighlight}
                        entries={wikiEntries || []}
                        onChanged={loadWiki}
                    />
                </TabsContent>

                <TabsContent value="fotos">
                    <PhotoTimeline
                        entries={wikiEntries}
                        error={wikiError}
                        onOpenEntry={(entryId) => { setWikiHighlight(entryId); setTab("wiki"); }}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default PropertyDetail;
